import { useState, FormEvent, useEffect, useRef } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('organization', formData.organization);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6fn9qhghdq4qda6vr60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="relative py-16 md:py-32 px-4 md:px-6 bg-deep-black overflow-hidden">
      {/* 顶部渐变：与 Testimonials 区平滑衔接 */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '140px',
          background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left Content */}
          <div className={`transform transition-all duration-1000 ease-out ${
            isVisible 
              ? 'translate-x-0 opacity-100' 
              : '-translate-x-8 opacity-0'
          }`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-champagne-gold/40 rounded-sm mb-4 md:mb-6">
              <i className="ri-mail-line text-xs" style={{ color: '#c9a84c' }}></i>
              <span className="font-cormorant text-xs tracking-widest" style={{ color: '#c9a84c', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>CONTACT</span>
            </div>

            <h2 className="text-3xl md:text-6xl font-cinzel-decorative font-bold mb-4 md:mb-6 leading-tight" style={{ color: '#c9a84c', textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}>
              <span className="block">A Note of Invitation</span>
            </h2>

            <p className="font-cormorant text-sm md:text-lg leading-relaxed mb-6 md:mb-8" style={{ color: '#ede8d8', opacity: 0.85, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
              Whether you represent an opera house, a production company, an agency, or are a colleague, a passionate lover of classical music, or an aspiring singer or performer eager to learn, I would be delighted to connect. Let us explore the possibilities of artistic collaboration and share the beauty of vocal arts with audiences around the world.
            </p>

            <div className={`space-y-4 mb-8 md:mb-12 transform transition-all duration-1000 ease-out ${
              isVisible 
                ? 'translate-y-0 opacity-100' 
                : 'translate-y-4 opacity-0'
            }`} style={{ transitionDelay: '300ms' }}>
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-emerald-green/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg md:text-xl" style={{ color: '#c9a84c' }}></i>
                </div>
                <div>
                  <div className="font-cormorant text-xs md:text-sm" style={{ color: '#ede8d8', opacity: 0.6, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Email</div>
                  <a
                    href="mailto:weichenvictor@gmail.com"
                    className="font-cormorant text-sm md:text-base hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                    style={{ color: '#ede8d8', textShadow: '0 1px 4px rgba(0,0,0,0.5)', opacity: 0.85 }}
                  >
                    weichenvictor@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className={`flex items-center gap-3 transform transition-all duration-1000 ease-out ${
              isVisible 
                ? 'translate-y-0 opacity-100' 
                : 'translate-y-4 opacity-0'
            }`} style={{ transitionDelay: '500ms' }}>
              <span className="font-cormorant text-xs md:text-sm" style={{ color: '#ede8d8', opacity: 0.6 }}>Follow:</span>
              {[
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/weichen_victor/' },
                { icon: 'ri-youtube-line', href: 'https://www.youtube.com/@VicVII' },
                { icon: 'ri-facebook-line', href: 'https://www.facebook.com/wei.chen.980156/' },
                { icon: 'ri-linkedin-line', href: '#' },
              ].map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  target={item.href !== '#' ? '_blank' : undefined}
                  rel={item.href !== '#' ? 'nofollow noopener noreferrer' : undefined}
                  className={`w-9 h-9 md:w-10 md:h-10 rounded-full border border-champagne-gold/40 flex items-center justify-center hover:bg-champagne-gold hover:text-deep-black transition-all duration-300 cursor-pointer transform ${
                    isVisible 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-4 opacity-0'
                  }`}
                  style={{ 
                    color: '#c9a84c',
                    transitionDelay: `${600 + i * 100}ms`
                  }}
                >
                  <i className={item.icon}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className={`bg-emerald-green/10 rounded-lg p-6 md:p-10 border border-champagne-gold/20 transform transition-all duration-1000 ease-out ${
            isVisible 
              ? 'translate-x-0 opacity-100' 
              : 'translate-x-8 opacity-0'
          }`} style={{ transitionDelay: '200ms' }}>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-4 md:space-y-6">
                <div className={`transform transition-all duration-1000 ease-out ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-4 opacity-0'
                }`} style={{ transitionDelay: '400ms' }}>
                  <label htmlFor="name" className="block font-cormorant text-xs md:text-sm mb-1.5 md:mb-2" style={{ color: '#c9a84c', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-deep-black/50 border border-champagne-gold/30 rounded-md font-cormorant text-sm focus:outline-none focus:border-champagne-gold transition-colors duration-300"
                    style={{ color: '#ede8d8' }}
                    placeholder="Your full name"
                  />
                </div>

                <div className={`transform transition-all duration-1000 ease-out ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-4 opacity-0'
                }`} style={{ transitionDelay: '500ms' }}>
                  <label htmlFor="email" className="block font-cormorant text-xs md:text-sm mb-1.5 md:mb-2" style={{ color: '#c9a84c', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-deep-black/50 border border-champagne-gold/30 rounded-md font-cormorant text-sm focus:outline-none focus:border-champagne-gold transition-colors duration-300"
                    style={{ color: '#ede8d8' }}
                    placeholder="your@email.com"
                  />
                </div>

                <div className={`transform transition-all duration-1000 ease-out ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-4 opacity-0'
                }`} style={{ transitionDelay: '600ms' }}>
                  <label htmlFor="organization" className="block font-cormorant text-xs md:text-sm mb-1.5 md:mb-2" style={{ color: '#c9a84c', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                    Organization / Company
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-deep-black/50 border border-champagne-gold/30 rounded-md font-cormorant text-sm focus:outline-none focus:border-champagne-gold transition-colors duration-300"
                    style={{ color: '#ede8d8' }}
                    placeholder="Your organization or company"
                  />
                </div>

                <div className={`transform transition-all duration-1000 ease-out ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-4 opacity-0'
                }`} style={{ transitionDelay: '700ms' }}>
                  <label htmlFor="message" className="block font-cormorant text-xs md:text-sm mb-1.5 md:mb-2" style={{ color: '#c9a84c', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-deep-black/50 border border-champagne-gold/30 rounded-md font-cormorant text-sm focus:outline-none focus:border-champagne-gold transition-colors duration-300 resize-none"
                    style={{ color: '#ede8d8' }}
                    placeholder="Tell me about your collaboration inquiry (max 500 characters)"
                  ></textarea>
                  <div className="text-right font-cormorant text-xs mt-1" style={{ color: '#ede8d8', opacity: 0.4 }}>
                    {formData.message.length}/500
                  </div>
                </div>

                <div className={`transform transition-all duration-1000 ease-out ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-4 opacity-0'
                }`} style={{ transitionDelay: '800ms' }}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 md:px-8 md:py-4 bg-champagne-gold text-deep-black rounded-full font-cormorant text-sm md:text-base font-semibold hover:bg-emerald-green hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>

                {submitStatus === 'success' && (
                  <div className="text-center font-cormorant text-sm" style={{ color: '#b8943a' }}>
                    Your message has been sent successfully! I will get back to you shortly.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="text-center text-red-400 font-cormorant text-sm">
                    Something went wrong. Please try again or contact me directly by email.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
